import { getAccessToken } from 'cng-common/helpers/access'
import { proxy } from 'core/classes/helpers/UrlHelper'
import { HttpHeaders, Rest } from 'core/classes/http'
import Logger from 'core/utils/Logger'

import { getFlagRecordAssignmentScope } from '../model/types'
import type { FlagRecordAssignmentCount } from '../model/types'

type FlagAssignmentCountParams = {
  code: string
  type: string
}

type FlagUsageCountProcedureRow = Record<string, unknown>

type FlagUsageCountProcedureResponse = {
  Data?: FlagUsageCountProcedureRow[]
  Total?: number
}

const FLAG_USAGE_COUNT_PROCEDURE_NAME = 'sp_WW_GetFlagUsageCount'
const FLAG_USAGE_COUNT_FIELD_NAMES = [
  'UsageCount',
  'FlagUsageCount',
  'Count',
  'COUNT',
  'Total',
  'TOTAL',
]

const normalizeText = (value?: unknown): string => {
  if (value === undefined || value === null) {
    return ''
  }

  return String(value).trim()
}

const parseCountValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return value
  }

  if (typeof value !== 'string') {
    return null
  }

  const normalizedValue = value.trim().replace(/,/g, '')

  if (!normalizedValue) {
    return null
  }

  const parsedValue = Number(normalizedValue)

  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : null
}

const getFlagUsageCountFromResponse = (response: FlagUsageCountProcedureResponse): number => {
  const row = response.Data?.[0]

  if (row) {
    for (const fieldName of FLAG_USAGE_COUNT_FIELD_NAMES) {
      const count = parseCountValue(row[fieldName])

      if (count !== null) {
        return count
      }
    }

    const count = Object.values(row)
      .map(parseCountValue)
      .find((value): value is number => value !== null)

    if (count !== undefined) {
      return count
    }
  }

  return parseCountValue(response.Total) ?? 0
}

const fetchFlagUsageCount = async (code: string, type: string): Promise<number> => {
  const url = proxy(`/webservice/197/sp/v4/${FLAG_USAGE_COUNT_PROCEDURE_NAME}/call`)
  const accessToken = getAccessToken()
  const headers = new HttpHeaders()

  if (accessToken) {
    headers.add('Authorization', `Bearer ${accessToken}`)
  }

  const response = (await Rest.post({
    url,
    payload: {
      'p.Code': code,
      'p.Type': type,
    },
    headers,
  })) as FlagUsageCountProcedureResponse

  return getFlagUsageCountFromResponse(response)
}

export const fetchFlagAssignmentCount = async ({
  code,
  type,
}: FlagAssignmentCountParams): Promise<FlagRecordAssignmentCount | null> => {
  const normalizedCode = normalizeText(code)
  const normalizedType = normalizeText(type)
  const scope = getFlagRecordAssignmentScope(normalizedType)

  if (!normalizedCode || !normalizedType || !scope) {
    return null
  }

  return fetchFlagUsageCount(normalizedCode, normalizedType)
    .then((count) => {
      return {
        ...scope,
        count,
      }
    })
    .catch((error) => {
      Logger.error('Error fetching flag assignment count:', error)
      throw error
    })
}
